import { getAuthHeaders } from '../../utils/token';
import { api } from '../Users/user';

const BASE_URL = api + '/api/pacientes';

/** Opciones de Género para el paciente */
export type PatientGender = 'MASCULINO' | 'FEMENINO' | 'OTRO' | 'NO_ESPECIFICADO';

/** Interfaz para el paciente devuelto por la API (con los nuevos campos) */
export interface PatientAPI {
  _id: string;
  patientId: string;
  firstName: string;
  lastName: string;
  gender: PatientGender;
  dateOfBirth: string;
  phoneNumber?: string;
  address?: string;
  emergencyContact?: {
    name: string;
    phone: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/** Interfaz para los datos necesarios para crear un nuevo paciente */
export interface PatientCreateData {
  patientId: string;
  firstName: string;
  lastName: string;
  gender: PatientGender;
  dateOfBirth: string;
  phoneNumber?: string;
  address?: string;
  emergencyContact?: {
    name: string;
    phone: string;
  };
  notes?: string;
}

/**
 * Función genérica para manejar peticiones Fetch, parsear JSON y lanzar errores si falla.
 */
async function apiFetch<T>(url: string, options: RequestInit): Promise<T> {
  const defaultHeaders = getAuthHeaders();
  options.headers = {
    ...defaultHeaders,
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const response = await fetch(url, options);

  const data = await response
    .json()
    .catch(() => ({ message: response.statusText || 'Error desconocido' }));

  if (!response.ok) {
    throw new Error(data.message || `La petición falló con status ${response.status}`);
  }

  return data as T;
}

/**
 * Obtiene la lista de todos los pacientes. (Asume que está protegido por checkAdmin)
 */
export function fetchAllPatients(): Promise<PatientAPI[]> {
  return apiFetch<PatientAPI[]>(BASE_URL, {
    method: 'GET',
  });
}

/**
 * Crea un nuevo paciente.
 * @param patientData Datos del nuevo paciente.
 */
export function createPatient(patientData: PatientCreateData): Promise<PatientAPI> {
  return apiFetch<PatientAPI>(BASE_URL, {
    method: 'POST',
    body: JSON.stringify(patientData),
  });
}

/**
 * Obtiene un paciente por su ID de MongoDB.
 * @param id ID de MongoDB del paciente.
 */
export function fetchPatientById(id: string): Promise<PatientAPI> {
  return apiFetch<PatientAPI>(`${BASE_URL}/mongo-id/${id}`, {
    method: 'GET',
  });
}

/**
 * Obtiene un paciente por su Patient ID (código ingresado por el usuario).
 * @param patientId Código único del paciente.
 */
export function fetchPatientByCode(patientId: string): Promise<PatientAPI> {
  return apiFetch<PatientAPI>(`${BASE_URL}/code/${patientId}`, {
    method: 'GET',
  });
}

/**
 * Actualiza parcialmente un paciente existente.
 * Se usa PATCH para enviar solo los campos que cambian.
 * @param id ID de MongoDB del paciente.
 * @param patientData Datos a actualizar.
 */
export function updatePatient(
  id: string,
  patientData: Partial<PatientCreateData>
): Promise<PatientAPI> {
  return apiFetch<PatientAPI>(`${BASE_URL}/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patientData),
  });
}

/**
 * Elimina un paciente por su ID.
 * @param id ID de MongoDB del paciente a eliminar.
 */
export function deletePatient(id: string): Promise<void> {
  return apiFetch<void>(`${BASE_URL}/${id}`, {
    method: 'DELETE',
  });
}
